/**
 * disposal_daily_email_recipients — Phase 16 / Task 16.4 (DEC-161).
 *
 * Multi-recipient list per (disposal_company, facility|digest) scope,
 * admin-managed by DISPOSAL_ADMIN users of the disposal tenant. Backs the
 * `module_daily_email` toggle (DEC-005 / DEC-158) — when the module is on,
 * the daily-email sweeper reads active rows from this table (via a
 * SECURITY DEFINER function) and dispatches emails on schedule.
 *
 * Scope semantics (mid-flight DEC-161 addition):
 *   - facility_id NOT NULL → recipient gets ONLY that facility's daily email
 *     (site-level: site managers, operations leads on the ground).
 *   - facility_id     NULL → recipient gets ONE consolidated digest per day
 *     covering ALL of the company's facilities (corporate / accounting).
 *
 * App-layer FK guard: Postgres does NOT allow a CHECK subquery to enforce
 * that facility_id belongs to disposal_company_id. The create-recipient
 * route validates via SELECT before INSERT (DISPOSAL_EMAIL_RECIPIENT_FACILITY_MISMATCH
 * on cross-company reference).
 *
 * Uniqueness: partial UNIQUE index on (disposal_company_id, LOWER(email),
 * COALESCE(facility_id, zero-uuid)) WHERE opted_out_at IS NULL. Postgres
 * treats NULL as distinct in UNIQUE constraints; COALESCE to a zero-uuid
 * sentinel collapses the digest scope to a single distinct value. Adding
 * the same email as both a facility recipient AND a digest recipient is
 * allowed (different scopes → different sentinel).
 *
 * Soft-delete via opted_out_at + opted_out_reason. Two reasons:
 *   - UNSUBSCRIBE_TOKEN — recipient clicked the CAN-SPAM one-click link.
 *   - ADMIN_REMOVED     — DISPOSAL_ADMIN removed via DELETE /:id.
 * Re-adding the same email flips the row back (INSERT succeeds because the
 * partial UNIQUE filters `WHERE opted_out_at IS NULL` — an opted-out row
 * with the same key is NOT part of the uniqueness set).
 *
 * RLS: tenant-isolated via a subquery to disposal_companies.tenant_id. Only
 * DISPOSAL tenant admins reach this table via the app_user path; the sweeper
 * bypasses via a SECURITY DEFINER `platform_disposal_daily_email_recipients`
 * function (migrator-owned; cross-tenant read).
 */

import {
  index,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  check,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { disposalCompanies, disposalFacilities } from './disposals.js';
import { users } from './users.js';

export const disposalDailyEmailRecipients = pgTable(
  'disposal_daily_email_recipients',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    disposalCompanyId: uuid('disposal_company_id')
      .notNull()
      .references(() => disposalCompanies.id, { onDelete: 'cascade' }),
    // NULL = digest recipient (all facilities); non-NULL = facility-scoped.
    // App-layer route guards cross-company references (no CHECK subquery in PG).
    facilityId: uuid('facility_id').references(() => disposalFacilities.id, {
      onDelete: 'cascade',
    }),
    email: text('email').notNull(),
    addedAt: timestamp('added_at', { withTimezone: true }).notNull().defaultNow(),
    addedByUserId: uuid('added_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    optedOutAt: timestamp('opted_out_at', { withTimezone: true }),
    optedOutReason: text('opted_out_reason'),
  },
  (t) => [
    // Lowercase enforcement — mirror of the DB CHECK; also enforced at the
    // Zod boundary. Rationale: recipients are user-managed identifiers whose
    // canonical form is lowercase (per §27 email semantics).
    check('disposal_daily_email_recipients_email_lower_check', sql`LOWER(email) = email`),
    check(
      'disposal_daily_email_recipients_reason_check',
      sql`opted_out_reason IN ('UNSUBSCRIBE_TOKEN', 'ADMIN_REMOVED') OR opted_out_reason IS NULL`,
    ),
    // Partial UNIQUE — zero-uuid sentinel collapses NULL facility_id to one
    // "digest" scope. Excludes opted-out rows so re-adding after unsubscribe
    // works via a fresh row.
    uniqueIndex('disposal_daily_email_recipients_active_idx')
      .on(
        t.disposalCompanyId,
        sql`LOWER(email)`,
        sql`COALESCE(facility_id, '00000000-0000-0000-0000-000000000000'::uuid)`,
      )
      .where(sql`opted_out_at IS NULL`),
    index('disposal_daily_email_recipients_company_idx')
      .on(t.disposalCompanyId)
      .where(sql`opted_out_at IS NULL`),
    pgPolicy('disposal_daily_email_recipients_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`disposal_company_id IN (
        SELECT dc.id FROM disposal_companies dc
        WHERE dc.tenant_id::text = current_setting('app.current_tenant_id', true)
      )`,
    }),
  ],
);

export type DisposalDailyEmailRecipient = typeof disposalDailyEmailRecipients.$inferSelect;
export type NewDisposalDailyEmailRecipient = typeof disposalDailyEmailRecipients.$inferInsert;
export const disposalDailyEmailRecipientSelectSchema = createSelectSchema(
  disposalDailyEmailRecipients,
);
export const disposalDailyEmailRecipientInsertSchema = createInsertSchema(
  disposalDailyEmailRecipients,
);
